import { DropdownDrawer, DropdownScrollContainer, DropdownSearch, Group, Label, Option } from '@tlx/atlas';
import { useId, useState } from 'react';
import { APIError, AsyncDropdownOptions, ValidationMessage } from '@tlx/astro-shared';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSharedTracking } from '@tlx/beehive-shared';
import { APIUrls } from '@Util/urls';
import { SalaryOnboardingTips } from '../SalaryOnboardingTips';
import { SalaryOnboardingStageForm } from '../SalaryOnboardingStage';
import {
    createPensionScheme,
    PensionSchemeDTO,
    updatePensionScheme,
    useLatestPensionSchemeOrDefault,
} from '../hooks/useLatestPensionSchemeOrDefault';
import { OnboardingStageSkeleton } from '../components/OnboardingStageSkeleton';
import { ValidatedDropdown } from '../components/ValidatedForm/ValidatedDropdown';
import { setCustomValidityOnField } from '../components/ValidatedForm/setCustomValidityOnField';
import { SalaryOnboardingTipsContainer } from '../SalaryOnboardingWithTips';

function mapToPensionSchemeValues(dto: PensionSchemeDTO) {
    return {
        pensionSchemeId: String(dto.pensionSchemeId),
        startDate: dto.startDate,
    };
}

function mapToPensionSchemeDTO(formData: Partial<PensionSchemeFormValues>) {
    return {
        pensionSchemeId: formData.pensionSchemeId ? Number(formData.pensionSchemeId) : undefined,
        startDate: formData.startDate,
    };
}

export function ConnectedPensionStage() {
    const { data, isLoading, mutate } = useLatestPensionSchemeOrDefault();
    if (!data || isLoading) {
        return <OnboardingStageSkeleton />;
    }

    const id = data.id;

    const defaultValue = mapToPensionSchemeValues(data);

    return (
        <PensionStage
            defaultValue={defaultValue}
            handleError={(form, error) => {
                const unhandled: ValidationMessage[] = [];
                error.details.validationMessages?.forEach((message) => {
                    if (message.field) {
                        const handled = setCustomValidityOnField(form, message.field, message.message);

                        if (!handled) {
                            unhandled.push(message);
                        }
                    }
                });
                return unhandled;
            }}
            handleSubmit={async (formData) => {
                if (formData.pensionSchemeId === '0') {
                    return Promise.resolve();
                }

                const dto = mapToPensionSchemeDTO(formData);
                if (id === 0) {
                    const response = await createPensionScheme(dto);
                    await mutate((listResponse) =>
                        listResponse
                            ? {
                                  ...listResponse,
                                  values: [...listResponse.values, response.value],
                              }
                            : undefined,
                    );
                } else {
                    await updatePensionScheme(id, dto);
                    await mutate();
                }
            }}
        />
    );
}

type PensionSchemeFormValues = {
    pensionSchemeId: string;
    startDate: string;
};

type PensionStageProps = {
    defaultValue: PensionSchemeFormValues;
    handleSubmit?: (formData: Partial<PensionSchemeFormValues>) => Promise<void>;
    handleError?: (form: HTMLFormElement, error: APIError) => ValidationMessage[] | void;
};
export function PensionStage({ defaultValue, handleError, handleSubmit }: PensionStageProps) {
    const selectId = useId();
    const intl = useIntl();
    const [pensionSchemeId, setPensionSchemeId] = useState(defaultValue.pensionSchemeId);
    const isPensionSchemeSelected = pensionSchemeId !== '0';

    return (
        <>
            <SalaryOnboardingStageForm
                title={intl.formatMessage({
                    id: 'tsk_onboarding_pension_title',
                })}
                description={intl.formatMessage({
                    id: 'tsk_onboarding_pension_description',
                })}
                handleSubmit={handleSubmit}
                handleError={handleError}
            >
                <Group>
                    <Label htmlFor={selectId}>
                        {intl.formatMessage({
                            id: 'tsk_onboarding_pension_label',
                        })}
                    </Label>
                    <ValidatedDropdown
                        id={selectId}
                        data-testid={useSharedTracking('pension-collaborator-dropdown')}
                        name="pensionSchemeId"
                        value={pensionSchemeId}
                        onChange={(e) => {
                            setPensionSchemeId(e.target.value);
                        }}
                    >
                        <DropdownDrawer>
                            <DropdownSearch />
                            <DropdownScrollContainer>
                                <Option value="0">{intl.formatMessage({ id: 'option_none' })}</Option>
                                <AsyncDropdownOptions
                                    url={`${APIUrls.SALARY_PENSION}?startDate=${defaultValue.startDate}`}
                                />
                            </DropdownScrollContainer>
                        </DropdownDrawer>
                    </ValidatedDropdown>
                </Group>
                {/* Tom Kong 2023-12-22: Use a hidden input field to handle default start date or existing start date*/}
                {isPensionSchemeSelected && (
                    <input
                        className="atl-w-full atl-box-border"
                        id="pensionSchemeStartDate"
                        type="hidden"
                        name="startDate"
                        defaultValue={defaultValue.startDate}
                        disabled={!isPensionSchemeSelected}
                    />
                )}
            </SalaryOnboardingStageForm>
            <SalaryOnboardingTipsContainer stageId="pension">
                <SalaryOnboardingTips
                    title={intl.formatMessage({
                        id: 'tsk_onboarding_pension_tip_title',
                    })}
                    tag="Tips"
                >
                    <FormattedMessage id={'tsk_onboarding_pension_tip_description'} />
                </SalaryOnboardingTips>
            </SalaryOnboardingTipsContainer>
        </>
    );
}
